import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './CalendarPage.module.css';
import { CalendarSearchPicker, CalendarSearchPickerOption } from './CalendarSearchPicker';
import { CalendarEventsListModal } from './CalendarEventsListModal';
import { CalendarAvailabilitySettingsModal } from './CalendarAvailabilitySettingsModal';
import { CalendarEventRecurrenceFields } from './CalendarEventRecurrenceFields';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import {
  createEvent,
  deleteEvent,
  getMyEmployee,
  getAvailability,
  getEventById,
  listAppointmentTypes,
  listConsultations,
  listEmployees,
  listEventStatuses,
  listEvents,
  updateEvent,
} from '../services/calendarService';
import {
  FALLBACK_CALENDAR_EVENT_STATUSES,
  getCalendarEventStatusLabel,
  getDefaultCalendarEventStatusCode,
  normalizeCalendarEventStatusCode,
} from '../utils/calendarEventStatuses';
import type { CalendarEventStatusDefinition } from '../types/Calendar';
import {
  listLocations,
  listOpeningHours,
  listRoomGroups,
  listRooms,
} from '../../companies/services/companyService';
import { VisitPlaceFields } from '../../field-operations/components/VisitPlaceFields';
import type { FieldVisitPlace } from '../../field-operations/types/FieldOperations';
import { emptyVisitPlace } from '../../field-operations/visitPlace';
import { contactService } from '../../contacts/services/contactService';
import { assetService } from '../../assets/services/assetService';
import {
  AppointmentType,
  AvailabilitySlot,
  CalendarEvent,
  CalendarEventStatus,
  CalendarRecurrence,
  Employee,
} from '../types/Calendar';
import {
  formatTimeLabel,
  getCalendarEventDisplayTitle,
  normalizeOptionalId,
  resolveEventAssetIds,
  resolveEventStaffIds,
  sortEventsByStart,
} from '../calendarUtils';
import {
  AVAILABILITY_LIST_PAGE_SIZE,
  addDurationMinutesToDate,
  buildAvailabilityFromLocal,
  clampEventDurationMinutes,
  getAvailabilitySlotDurationMinutes,
  getDurationMinutesBetweenDates,
  groupAvailabilitySlotsByDay,
  mergeAvailabilitySlots,
  resolveAvailabilityQueryMinutes,
} from '../availabilityListUtils';
import { CompanyOpeningHour } from '../../companies/types/Company';
import { Contact } from '../../contacts/types/Contact';
import { Asset, assetTypeLabelKey, getPrimaryOwnerId } from '../../assets/types/Asset';
import { LocalizedDateInput, LocalizedDateTimeInput, Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import { formatLocalizedDateTimeLabel, formatDateTimeInputValue, parseDateTimeInputValue } from '../../../shared/utils/localization';
import { resolveEventConsultationNavigation } from '../../consultations/utils/eventConsultationNavigation';
import {
  MONDAY_FIRST_WEEKDAY_KEYS,
  MONTH_I18N_KEYS,
  formatWeekDayColumnLabel,
} from '../../rostering/utils/rosterDateLabels';
import { formatAvailabilityDayHeadingLabel } from '../../../shared/utils/calendarPickerLabels';
import {
  buildRecurrencePayload,
  DEFAULT_CALENDAR_RECURRENCE,
  formatRecurrenceSummary,
  hasActiveRecurrence,
  recurrenceFromEvent,
} from '../utils/calendarRecurrenceUtils';

const toLocalInputValue = (date: Date) => formatDateTimeInputValue(date);

const parseLocalDateTime = (value: string): Date | null => parseDateTimeInputValue(value);

const toUtcIso = (localDateTimeValue: string) => {
  const parsed = parseLocalDateTime(localDateTimeValue);
  if (!parsed) return '';
  return parsed.toISOString();
};

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDateInputValue = (date: Date) => {
  return formatDateTimeInputValue(date).slice(0, 10);
};

const startOfWeek = (date: Date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const dayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayOffset);
  return start;
};

const getTimelineStatusClass = (status: CalendarEventStatus, stylesObj: Record<string, string>) => {
  const normalized = normalizeCalendarEventStatusCode(status);
  if (normalized === 'confirmed') return stylesObj.timeSlotEventConfirmed;
  if (normalized === 'tentative' || normalized === 'draft') return stylesObj.timeSlotEventTentative;
  if (normalized === 'cancelled' || normalized === 'noShow') return stylesObj.timeSlotEventCancelled;
  if (normalized === 'completed' || normalized === 'inProgress' || normalized === 'inWaitingRoom') {
    return stylesObj.timeSlotEventConfirmed;
  }
  return '';
};

type CalendarViewMode = 'week' | 'day';
type SidePanelMode = 'create' | 'availability';
const UNASSIGNED_ROOM_KEY = '__unassigned_room__';
const DEFAULT_EVENT_DURATION_MINUTES = 20;
const ADDITIONAL_STAFF_PAGE_SIZE = 120;
const ADDITIONAL_CONTACT_PAGE_SIZE = 120;
const ADDITIONAL_ASSET_PAGE_SIZE = 120;

type TimeSlot = {
  key: string;
  label: string;
  events: CalendarEvent[];
  startAt: Date;
  endAt: Date;
  isHourBoundary?: boolean;
};

type OpeningWindow = {
  startMinute: number;
  endMinute: number;
};

const parseTimeToMinutes = (timeValue?: string): number | null => {
  const rendered = (timeValue || '').trim();
  if (!rendered) return null;

  const match = rendered.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return (hours * 60) + minutes;
};

export const CalendarPage: React.FC = () => {
  const location = useLocation();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useTranslation(['calendar', 'common', 'consultations', 'assets']);
  const translateCalendarDateLabel = React.useCallback(
    (key: string) => t(key, { ns: 'calendar' }),
    [t],
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canAccessConsultations = hasPermission(PermissionScopes.consultationsRead);
  const canManageConsultationTypes = hasPermission(PermissionScopes.consultationsManage);
  const canManageEventStatuses = hasPermission(PermissionScopes.calendarStatusesManage);
  const canManageCalendar = hasPermission(PermissionCodes.calendarManage);
  const quickIntervals = [15, 30, 60];
  const [eventStatuses, setEventStatuses] = React.useState<CalendarEventStatusDefinition[]>(FALLBACK_CALENDAR_EVENT_STATUSES);
  const activeEventStatuses = React.useMemo(
    () => eventStatuses.filter((item) => item.status === 'active'),
    [eventStatuses],
  );
  const statusOptions = React.useMemo<Array<{ value: CalendarEventStatus; label: string }>>(() => {
    const source = activeEventStatuses.length > 0 ? activeEventStatuses : FALLBACK_CALENDAR_EVENT_STATUSES;
    return source.map((item) => ({
      value: item.code,
      label: getCalendarEventStatusLabel(item.code, (key) => t(key), source),
    }));
  }, [activeEventStatuses, t]);
  const defaultEventStatus = React.useMemo(
    () => getDefaultCalendarEventStatusCode(activeEventStatuses.length > 0 ? activeEventStatuses : FALLBACK_CALENDAR_EVENT_STATUSES),
    [activeEventStatuses],
  );

  const [fromLocal, setFromLocal] = React.useState(() => toLocalInputValue(startOfWeek(new Date())));
  const [toLocal, setToLocal] = React.useState(() => {
    const weekStart = startOfWeek(new Date());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 0, 0);
    return toLocalInputValue(weekEnd);
  });
  const [eventRoomId, setEventRoomId] = React.useState('');
  const [slotMinutes, setSlotMinutes] = React.useState(20);
  const [employeesLoading, setEmployeesLoading] = React.useState(false);
  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [myEmployeeId, setMyEmployeeId] = React.useState<string | null>(null);
  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [assets, setAssets] = React.useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = React.useState(false);
  const [viewEventAssets, setViewEventAssets] = React.useState<Asset[]>([]);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [roomNameById, setRoomNameById] = React.useState<Record<string, string>>({});
  const [roomIdsByGroupId, setRoomIdsByGroupId] = React.useState<Record<string, string[]>>({});
  const [roomGroupOptions, setRoomGroupOptions] = React.useState<Array<{ id: string; label: string }>>([]);
  const [selectedRoomGroupId, setSelectedRoomGroupId] = React.useState('');
  const [businessHoursOnly, setBusinessHoursOnly] = React.useState(true);
  const [openingHours, setOpeningHours] = React.useState<CompanyOpeningHour[]>([]);

  const [availabilityLoading, setAvailabilityLoading] = React.useState(false);
  const [availabilityLoadingMore, setAvailabilityLoadingMore] = React.useState(false);
  const [availabilitySlots, setAvailabilitySlots] = React.useState<AvailabilitySlot[]>([]);
  const [availabilityPage, setAvailabilityPage] = React.useState(1);
  const [availabilityHasNext, setAvailabilityHasNext] = React.useState(false);

  const [eventsLoading, setEventsLoading] = React.useState(false);
  const [events, setEvents] = React.useState<CalendarEvent[]>([]);

  const [eventTitle, setEventTitle] = React.useState('');
  const [eventNotes, setEventNotes] = React.useState('');
  const [eventVisitPlace, setEventVisitPlace] = React.useState<FieldVisitPlace | undefined>(undefined);
  const [eventStatus, setEventStatus] = React.useState<CalendarEventStatus>('confirmed');
  const [eventRecurrence, setEventRecurrence] = React.useState<CalendarRecurrence>(() => DEFAULT_CALENDAR_RECURRENCE());
  const [applyToSeries, setApplyToSeries] = React.useState(false);
  const [eventAllowOverlap, setEventAllowOverlap] = React.useState(false);
  const [eventSelectedStaffIds, setEventSelectedStaffIds] = React.useState<string[]>([]);
  const [staffSearch, setStaffSearch] = React.useState('');
  const [staffVisibleCount, setStaffVisibleCount] = React.useState(ADDITIONAL_STAFF_PAGE_SIZE);
  const [isStaffPickerOpen, setIsStaffPickerOpen] = React.useState(false);
  const [eventContactId, setEventContactId] = React.useState('');
  const [contactSearch, setContactSearch] = React.useState('');
  const [contactVisibleCount, setContactVisibleCount] = React.useState(ADDITIONAL_CONTACT_PAGE_SIZE);
  const [isContactPickerOpen, setIsContactPickerOpen] = React.useState(false);
  const [eventSelectedAssetIds, setEventSelectedAssetIds] = React.useState<string[]>([]);
  const [assetSearch, setAssetSearch] = React.useState('');
  const [assetVisibleCount, setAssetVisibleCount] = React.useState(ADDITIONAL_ASSET_PAGE_SIZE);
  const [isAssetPickerOpen, setIsAssetPickerOpen] = React.useState(false);
  const [eventAppointmentTypeId, setEventAppointmentTypeId] = React.useState('');
  const [eventDurationMinutes, setEventDurationMinutes] = React.useState(DEFAULT_EVENT_DURATION_MINUTES);
  const [eventStartLocal, setEventStartLocal] = React.useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return toLocalInputValue(d);
  });
  const [editingEvent, setEditingEvent] = React.useState<CalendarEvent | null>(null);

  const [calendarWeekStart, setCalendarWeekStart] = React.useState(() => startOfWeek(new Date()));
  const [calendarDay, setCalendarDay] = React.useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const [calendarViewMode, setCalendarViewMode] = React.useState<CalendarViewMode>('week');
  const [isSidePanelOpen, setIsSidePanelOpen] = React.useState(false);
  const [sidePanelMode, setSidePanelMode] = React.useState<SidePanelMode>('create');
  const [activeEventMenuKey, setActiveEventMenuKey] = React.useState<string | null>(null);
  const [viewEvent, setViewEvent] = React.useState<CalendarEvent | null>(null);
  const [viewEventHasConsultation, setViewEventHasConsultation] = React.useState(false);

  React.useEffect(() => {
    const eventId = searchParams.get('eventId')?.trim();
    if (!eventId) {
      return;
    }

    let cancelled = false;

    const openLinkedEvent = async () => {
      try {
        const event = await getEventById(eventId);
        if (!cancelled && event) {
          setViewEvent(event);
        }
      } catch {
        if (!cancelled) {
          setViewEvent(null);
        }
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete('eventId');
          setSearchParams(next, { replace: true });
        }
      }
    };

    void openLinkedEvent();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  const [isEventsListOpen, setIsEventsListOpen] = React.useState(false);
  const [isAvailabilitySettingsOpen, setIsAvailabilitySettingsOpen] = React.useState(false);
  const eventRoomSelection = normalizeOptionalId(eventRoomId);
  const eventStaffIdsValue = React.useMemo(() => {
    return Array.from(new Set(eventSelectedStaffIds.map(normalizeOptionalId).filter((value): value is string => Boolean(value))));
  }, [eventSelectedStaffIds]);
  const eventStaffSelection = eventStaffIdsValue[0];
  const eventContactSelection = normalizeOptionalId(eventContactId);
  const eventAssetIdsValue = React.useMemo(() => {
    return Array.from(new Set(eventSelectedAssetIds.map(normalizeOptionalId).filter((value): value is string => Boolean(value))));
  }, [eventSelectedAssetIds]);
  const eventAssetSelection = eventAssetIdsValue[0];
  const eventAppointmentTypeSelection = normalizeOptionalId(eventAppointmentTypeId);
  const availabilityQueryMinutes = React.useMemo(
    () => resolveAvailabilityQueryMinutes({
      slotMinutes,
      durationMinutes: eventDurationMinutes,
    }),
    [eventDurationMinutes, slotMinutes],
  );

  const activeAnchorDate = calendarViewMode === 'week' ? calendarWeekStart : calendarDay;

  const getOpeningRulesForDate = React.useCallback((date: Date): CompanyOpeningHour[] => {
    if (openingHours.length === 0) {
      return [];
    }

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dateKey = toDateInputValue(dateOnly);
    const dayIndex = dateOnly.getDay();

    return openingHours.filter((rule) => {
      if (!rule.isEnabled) return false;
      if (rule.daysOfWeek.length > 0 && !rule.daysOfWeek.includes(dayIndex)) return false;
      if (rule.startDate && dateKey < rule.startDate) return false;
      if (rule.endDate && dateKey > rule.endDate) return false;
      return true;
    });
  }, [openingHours]);

  const getOpeningStartMinute = React.useCallback((date: Date): number | null => {
    const matchingStartMinutes = getOpeningRulesForDate(date)
      .map((rule) => (rule.isAllDay ? 0 : parseTimeToMinutes(rule.startTime)))
      .filter((value): value is number => value !== null);

    if (matchingStartMinutes.length === 0) {
      return null;
    }

    return Math.min(...matchingStartMinutes);
  }, [getOpeningRulesForDate]);

  const getOpeningEndMinute = React.useCallback((date: Date): number | null => {
    const matchingEndMinutes = getOpeningRulesForDate(date)
      .map((rule) => (rule.isAllDay ? (24 * 60) : parseTimeToMinutes(rule.endTime)))
      .filter((value): value is number => value !== null);

    if (matchingEndMinutes.length === 0) {
      return null;
    }

    return Math.max(...matchingEndMinutes);
  }, [getOpeningRulesForDate]);

  // Individual open windows, so a day split into morning and afternoon shifts stays split.
  const getOpeningWindowsForDate = React.useCallback((date: Date): OpeningWindow[] => (
    getOpeningRulesForDate(date)
      .map((rule) => (rule.isAllDay
        ? { startMinute: 0, endMinute: 24 * 60 }
        : { startMinute: parseTimeToMinutes(rule.startTime), endMinute: parseTimeToMinutes(rule.endTime) }))
      .filter((window): window is OpeningWindow => (
        window.startMinute !== null
        && window.endMinute !== null
        && window.endMinute > window.startMinute
      ))
  ), [getOpeningRulesForDate]);

  // Until opening hours are configured there is nothing to contrast against, so nothing is marked.
  const hasOpeningHours = openingHours.length > 0;

  const isOutsideBusinessHours = React.useCallback((startAt: Date, endAt: Date) => {
    if (!hasOpeningHours) {
      return false;
    }

    const windows = getOpeningWindowsForDate(startAt);
    if (windows.length === 0) {
      return true;
    }

    const startMinute = (startAt.getHours() * 60) + startAt.getMinutes();
    const rawEndMinute = (endAt.getHours() * 60) + endAt.getMinutes();
    // A slot ending at midnight rolls over to the next calendar day.
    const endMinute = rawEndMinute <= startMinute ? 24 * 60 : rawEndMinute;

    return !windows.some((window) => startMinute < window.endMinute && window.startMinute < endMinute);
  }, [getOpeningWindowsForDate, hasOpeningHours]);

  const isClosedAllDay = React.useCallback((date: Date) => (
    hasOpeningHours && getOpeningWindowsForDate(date).length === 0
  ), [getOpeningWindowsForDate, hasOpeningHours]);

  const availabilityFromLocal = React.useMemo(
    () => buildAvailabilityFromLocal(activeAnchorDate, getOpeningStartMinute(activeAnchorDate)),
    [activeAnchorDate, getOpeningStartMinute],
  );

  const eventEndLocal = React.useMemo(() => {
    const startAt = parseLocalDateTime(eventStartLocal);
    if (!startAt) {
      return '';
    }

    return toLocalInputValue(addDurationMinutesToDate(startAt, eventDurationMinutes));
  }, [eventDurationMinutes, eventStartLocal]);

  const eventTitleValue = eventTitle.trim() || undefined;
  const eventNotesValue = eventNotes.trim() || undefined;

  const getStatusLabel = React.useCallback((status: CalendarEventStatus) => {
    return getCalendarEventStatusLabel(status, (key) => t(key), eventStatuses);
  }, [eventStatuses, t]);

  React.useEffect(() => {
    if (!viewEvent?.id || !canAccessConsultations) {
      setViewEventHasConsultation(false);
      return;
    }

    let cancelled = false;

    const loadConsultationState = async () => {
      const resolvedAssetIds = resolveEventAssetIds(viewEvent!);

      try {
        const consultations = await listConsultations({
          eventId: viewEvent.id,
          assetId: resolvedAssetIds[0] || undefined,
          contactId: viewEvent.contactId || undefined,
        });

        if (!cancelled) {
          setViewEventHasConsultation(consultations.length > 0);
        }
      } catch {
        if (!cancelled) {
          setViewEventHasConsultation(false);
        }
      }
    };

    void loadConsultationState();

    return () => {
      cancelled = true;
    };
  }, [canAccessConsultations, viewEvent]);

  const loadAvailability = React.useCallback(async (options?: { silent?: boolean; page?: number; append?: boolean }) => {
    const fromUtc = toUtcIso(availabilityFromLocal);
    const silent = Boolean(options?.silent);
    const page = options?.page ?? 1;
    const append = Boolean(options?.append);

    if (!fromUtc) {
      if (!silent) {
        showToast(t('availabilityRangeInvalid'), 'error');
      }
      return;
    }

    if (append) {
      setAvailabilityLoadingMore(true);
    } else {
      setAvailabilityLoading(true);
    }

    try {
      const result = await getAvailability({
        fromUtc,
        slotMinutes: availabilityQueryMinutes.slotMinutes,
        durationMinutes: availabilityQueryMinutes.durationMinutes,
        roomId: eventRoomSelection,
        staffId: eventStaffSelection,
        appointmentTypeId: eventAppointmentTypeSelection,
        bookingChannel: 'general',
        page,
        pageSize: AVAILABILITY_LIST_PAGE_SIZE,
      });

      setAvailabilityPage(result.page);
      setAvailabilityHasNext(result.hasNext);
      setAvailabilitySlots((current) => (
        append ? mergeAvailabilitySlots(current, result.slots) : result.slots
      ));
    } catch (error) {
      if (!silent) {
        showToast(error instanceof Error ? error.message : t('failedToLoadAvailability'), 'error');
      }
    } finally {
      if (append) {
        setAvailabilityLoadingMore(false);
      } else {
        setAvailabilityLoading(false);
      }
    }
  }, [availabilityFromLocal, availabilityQueryMinutes, eventAppointmentTypeSelection, eventRoomSelection, eventStaffSelection, showToast, t]);

  const loadMoreAvailability = React.useCallback(async () => {
    if (availabilityLoading || availabilityLoadingMore || !availabilityHasNext) {
      return;
    }

    await loadAvailability({ page: availabilityPage + 1, append: true, silent: true });
  }, [availabilityHasNext, availabilityLoading, availabilityLoadingMore, availabilityPage, loadAvailability]);

  const loadEvents = React.useCallback(async () => {
    setEventsLoading(true);
    try {
      const data = await listEvents({
        fromUtc: toUtcIso(fromLocal),
        toUtc: toUtcIso(toLocal),
      });
      setEvents(data);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoadEvents'), 'error');
    } finally {
      setEventsLoading(false);
    }
  }, [fromLocal, showToast, t, toLocal]);

  const loadEmployees = React.useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const [allEmployees, me] = await Promise.all([listEmployees(), getMyEmployee()]);
      setEmployees(allEmployees);
      setMyEmployeeId(me?.id || null);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoadEmployees'), 'error');
    } finally {
      setEmployeesLoading(false);
    }
  }, [showToast, t]);

  const loadEventLookups = React.useCallback(async () => {
    try {
      const [allContacts, allAppointmentTypes, allEventStatuses] = await Promise.all([
        contactService.getContacts(),
        listAppointmentTypes({ status: 'active' }),
        listEventStatuses(),
      ]);

      setContacts(allContacts);
      setAppointmentTypes(allAppointmentTypes);
      setEventStatuses(allEventStatuses.length > 0 ? allEventStatuses : FALLBACK_CALENDAR_EVENT_STATUSES);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoadEvents'), 'error');
      setContacts([]);
      setAppointmentTypes([]);
      setEventStatuses(FALLBACK_CALENDAR_EVENT_STATUSES);
    }
  }, [showToast, t]);

  const loadRoomCatalog = React.useCallback(async () => {
    try {
      const locations = await listLocations({ isActive: true, pageSize: 200 });
      const locationNameById = locations.reduce<Record<string, string>>((acc, location) => {
        acc[location.id] = location.name || location.id;
        return acc;
      }, {});

      const roomGroupsByLocation = await Promise.all(
        locations.map(async (location) => ({
          locationId: location.id,
          groups: await listRoomGroups(location.id, { isActive: true, pageSize: 200 }),
        })),
      );

      const roomLists = await Promise.all(
        roomGroupsByLocation.flatMap(({ locationId, groups }) =>
          groups.map(async (group) => ({
            locationId,
            groupId: group.id,
            rooms: await listRooms(locationId, group.id, { isActive: true, pageSize: 200 }),
          }))),
      );

      const nextRoomNameById: Record<string, string> = {};
      const nextRoomIdsByGroupId: Record<string, string[]> = {};
      const nextRoomGroupOptions: Array<{ id: string; label: string }> = [];

      roomGroupsByLocation.forEach(({ locationId, groups }) => {
        groups.forEach((group) => {
          if (!group.id) return;
          nextRoomGroupOptions.push({
            id: group.id,
            label: `${locationNameById[locationId] || locationId} / ${group.name || group.id}`,
          });
          nextRoomIdsByGroupId[group.id] = [];
        });
      });

      roomLists.forEach(({ rooms }) => {
        rooms.forEach((room) => {
          if (!room.id) return;
          nextRoomNameById[room.id] = room.name || room.id;
          if (!room.roomGroupId) return;
          const currentRoomIds = nextRoomIdsByGroupId[room.roomGroupId] || [];
          if (!currentRoomIds.includes(room.id)) {
            nextRoomIdsByGroupId[room.roomGroupId] = [...currentRoomIds, room.id];
          }
        });
      });

      setRoomNameById(nextRoomNameById);
      setRoomIdsByGroupId(nextRoomIdsByGroupId);
      setRoomGroupOptions(nextRoomGroupOptions.sort((a, b) => a.label.localeCompare(b.label)));
    } catch {
      // Keep calendar usable even if room catalog lookup fails.
      setRoomNameById({});
      setRoomIdsByGroupId({});
      setRoomGroupOptions([]);
    }
  }, []);

  const loadOpeningHours = React.useCallback(async () => {
    try {
      const data = await listOpeningHours({ isEnabled: true });
      setOpeningHours(data || []);
    } catch {
      // Keep calendar usable if opening-hours lookup fails.
      setOpeningHours([]);
    }
  }, []);

  React.useEffect(() => {
    loadEvents();
    loadEmployees();
    loadEventLookups();
    loadRoomCatalog();
    loadOpeningHours();
  }, [loadEventLookups, loadEvents, loadEmployees, loadOpeningHours, loadRoomCatalog]);

  React.useEffect(() => {
    loadAvailability({ silent: true });
  }, [loadAvailability]);

  React.useEffect(() => {
    if (!eventContactSelection) {
      setAssets([]);
      setAssetsLoading(false);
      setEventSelectedAssetIds([]);
      setIsAssetPickerOpen(false);
      return undefined;
    }

    let cancelled = false;

    const loadContactAssets = async () => {
      setAssetsLoading(true);
      try {
        const loadedAssets = await assetService.getAssets({
          contactPersonId: eventContactSelection,
          pageSize: 200,
          sortBy: 'name',
          sortDir: 'asc',
        });

        if (!cancelled) {
          setAssets(loadedAssets);
        }
      } catch (error) {
        if (!cancelled) {
          setAssets([]);
          showToast(error instanceof Error ? error.message : t('failedToLoadAssets', { defaultValue: 'Failed to load assets.' }), 'error');
        }
      } finally {
        if (!cancelled) {
          setAssetsLoading(false);
        }
      }
    };

    void loadContactAssets();

    return () => {
      cancelled = true;
    };
  }, [eventContactSelection, showToast, t]);

  React.useEffect(() => {
    if (!viewEvent?.contactId) {
      setViewEventAssets([]);
      return undefined;
    }

    let cancelled = false;

    const loadViewEventAssets = async () => {
      try {
        const loadedAssets = await assetService.getAssets({
          contactPersonId: viewEvent.contactId,
          pageSize: 200,
        });

        if (!cancelled) {
          setViewEventAssets(loadedAssets);
        }
      } catch {
        if (!cancelled) {
          setViewEventAssets([]);
        }
      }
    };

    void loadViewEventAssets();

    return () => {
      cancelled = true;
    };
  }, [viewEvent?.contactId, viewEvent?.id]);

  const getRoomDisplayName = React.useCallback((eventRoomId?: string) => {
    const normalizedRoomId = (eventRoomId || '').trim();
    if (!normalizedRoomId) return t('unassigned');
    return roomNameById[normalizedRoomId] || t('unknownRoom', { defaultValue: 'Unknown room' });
  }, [roomNameById, t]);

  const roomGroupIdByRoomId = React.useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(roomIdsByGroupId).forEach(([groupId, roomIds]) => {
      roomIds.forEach((roomId) => {
        map[roomId] = groupId;
      });
    });
    return map;
  }, [roomIdsByGroupId]);

  const eventRoomOptions = React.useMemo(() => {
    const roomIds = selectedRoomGroupId
      ? (roomIdsByGroupId[selectedRoomGroupId] || [])
      : Object.keys(roomNameById);

    return Array.from(new Set(roomIds))
      .map((id) => ({ id, name: roomNameById[id] || id }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [roomIdsByGroupId, roomNameById, selectedRoomGroupId]);

  const toRoomColumnKey = React.useCallback((eventRoomId?: string) => {
    const normalizedRoomId = (eventRoomId || '').trim();
    return normalizedRoomId || UNASSIGNED_ROOM_KEY;
  }, []);

  React.useEffect(() => {
    if (!selectedRoomGroupId) return;
    const exists = roomGroupOptions.some((option) => option.id === selectedRoomGroupId);
    if (!exists) {
      setSelectedRoomGroupId('');
    }
  }, [roomGroupOptions, selectedRoomGroupId]);

  React.useEffect(() => {
    if (!eventRoomId) {
      return;
    }

    const exists = eventRoomOptions.some((option) => option.id === eventRoomId);
    if (!exists) {
      setEventRoomId('');
    }
  }, [eventRoomId, eventRoomOptions]);

  const getEmployeeLabel = React.useCallback((employee: Employee) => {
    const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
    const displayName = employee.name || fullName || employee.email || employee.id;
    return displayName;
  }, []);

  const getContactLabel = React.useCallback((contact: Contact) => {
    const displayName = contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || contact.id;
    return displayName;
  }, []);

  const getAssetLabel = React.useCallback((asset: Asset) => {
    const primaryContactId = getPrimaryOwnerId(asset);
    const primaryContactName = primaryContactId
      ? contacts.find(contact => contact.id === primaryContactId)?.name
      : undefined;
    const contactSuffix = primaryContactName ? ` / ${primaryContactName}` : '';
    const typeSuffix = asset.assetType ? ` (${t(assetTypeLabelKey(asset.assetType), { ns: 'assets', defaultValue: asset.assetType })})` : '';
    return `${asset.name}${typeSuffix}${contactSuffix}`;
  }, [contacts, t]);

  const getAppointmentTypeLabel = React.useCallback((appointmentType: AppointmentType) => {
    return appointmentType.name || appointmentType.id;
  }, []);

  const appointmentTypeLabelById = React.useMemo(() => {
    const labels: Record<string, string> = {};
    appointmentTypes.forEach((appointmentType) => {
      labels[appointmentType.id] = getAppointmentTypeLabel(appointmentType);
    });
    return labels;
  }, [appointmentTypes, getAppointmentTypeLabel]);

  const getEventDisplayTitle = React.useCallback((event: Pick<CalendarEvent, 'title' | 'appointmentTypeId'>) => (
    getCalendarEventDisplayTitle(event, appointmentTypeLabelById, t('untitledEvent'))
  ), [appointmentTypeLabelById, t]);

  const onChangeEventAppointmentType = React.useCallback((appointmentTypeId: string) => {
    setEventAppointmentTypeId(appointmentTypeId);

    const appointmentType = appointmentTypes.find((item) => item.id === appointmentTypeId);
    if (!appointmentType) {
      return;
    }

    setEventDurationMinutes(clampEventDurationMinutes(appointmentType.defaultDurationMinutes));
  }, [appointmentTypes]);

  const onChangeEventDurationMinutes = React.useCallback((value: string) => {
    setEventDurationMinutes(clampEventDurationMinutes(Number(value) || DEFAULT_EVENT_DURATION_MINUTES));
  }, []);

  const onChangeEventStartLocal = React.useCallback((value: string) => {
    setEventStartLocal(value);
  }, []);

  const getStaffNames = React.useCallback((staffIds?: string[]) => {
    if (!staffIds || staffIds.length === 0) {
      return '-';
    }

    return staffIds
      .map((staffId) => {
        const employee = employees.find((item) => item.id === staffId);
        return employee ? getEmployeeLabel(employee) : t('unknownEmployee', { defaultValue: 'Unknown employee' });
      })
      .join(', ');
  }, [employees, getEmployeeLabel, t]);

  const filteredAssetOptions = React.useMemo(() => {
    if (!eventContactSelection) {
      return [];
    }

    return assets;
  }, [assets, eventContactSelection]);

  const availableAdditionalStaffOptions = React.useMemo(() => {
    return employees;
  }, [employees]);

  const filteredAdditionalStaffOptions = React.useMemo(() => {
    const normalizedSearch = staffSearch.trim().toLowerCase();
    const selectedIds = new Set(eventSelectedStaffIds);

    const base = normalizedSearch
      ? availableAdditionalStaffOptions.filter((employee) => {
        const label = getEmployeeLabel(employee).toLowerCase();
        const email = (employee.email || '').toLowerCase();
        return label.includes(normalizedSearch) || email.includes(normalizedSearch);
      })
      : availableAdditionalStaffOptions;

    return [...base].sort((left, right) => {
      const leftSelectedWeight = selectedIds.has(left.id) ? 0 : 1;
      const rightSelectedWeight = selectedIds.has(right.id) ? 0 : 1;
      if (leftSelectedWeight !== rightSelectedWeight) {
        return leftSelectedWeight - rightSelectedWeight;
      }

      return getEmployeeLabel(left).localeCompare(getEmployeeLabel(right));
    });
  }, [staffSearch, availableAdditionalStaffOptions, eventSelectedStaffIds, getEmployeeLabel]);

  const visibleAdditionalStaffOptions = React.useMemo(() => {
    return filteredAdditionalStaffOptions.slice(0, staffVisibleCount);
  }, [filteredAdditionalStaffOptions, staffVisibleCount]);

  const hasMoreAdditionalStaff = filteredAdditionalStaffOptions.length > staffVisibleCount;

  const selectedAdditionalStaffOptions = React.useMemo(() => {
    const employeeById = new Map(employees.map((employee) => [employee.id, employee]));
    return eventSelectedStaffIds
      .map((staffId) => employeeById.get(staffId))
      .filter((employee): employee is Employee => Boolean(employee));
  }, [employees, eventSelectedStaffIds]);

  const toEmployeePickerOption = React.useCallback((employee: Employee): CalendarSearchPickerOption => {
    const label = getEmployeeLabel(employee);
    return {
      id: employee.id,
      label,
      meta: employee.email && employee.email !== label ? employee.email : undefined,
    };
  }, [getEmployeeLabel]);

  const visibleStaffPickerOptions = React.useMemo(() => {
    return visibleAdditionalStaffOptions.map(toEmployeePickerOption);
  }, [visibleAdditionalStaffOptions, toEmployeePickerOption]);

  const selectedStaffPickerOptions = React.useMemo(() => {
    return selectedAdditionalStaffOptions.map(toEmployeePickerOption);
  }, [selectedAdditionalStaffOptions, toEmployeePickerOption]);

  const filteredContactOptions = React.useMemo(() => {
    const normalizedSearch = contactSearch.trim().toLowerCase();
    const selectedIds = new Set(eventContactSelection ? [eventContactSelection] : []);

    const base = normalizedSearch
      ? contacts.filter((contact) => {
        const label = getContactLabel(contact).toLowerCase();
        const email = (contact.email || '').toLowerCase();
        return label.includes(normalizedSearch) || email.includes(normalizedSearch);
      })
      : contacts;

    return [...base].sort((left, right) => {
      const leftSelectedWeight = selectedIds.has(left.id) ? 0 : 1;
      const rightSelectedWeight = selectedIds.has(right.id) ? 0 : 1;
      if (leftSelectedWeight !== rightSelectedWeight) {
        return leftSelectedWeight - rightSelectedWeight;
      }

      return getContactLabel(left).localeCompare(getContactLabel(right));
    });
  }, [contacts, contactSearch, eventContactSelection, getContactLabel]);

  const visibleContactOptions = React.useMemo(() => {
    return filteredContactOptions.slice(0, contactVisibleCount);
  }, [contactVisibleCount, filteredContactOptions]);

  const hasMoreContacts = filteredContactOptions.length > contactVisibleCount;

  const toContactPickerOption = React.useCallback((contact: Contact): CalendarSearchPickerOption => {
    const label = getContactLabel(contact);
    return {
      id: contact.id,
      label,
      meta: contact.email && contact.email !== label ? contact.email : undefined,
    };
  }, [getContactLabel]);

  const visibleContactPickerOptions = React.useMemo(() => {
    return visibleContactOptions.map(toContactPickerOption);
  }, [visibleContactOptions, toContactPickerOption]);

  const selectedContactPickerOptions = React.useMemo(() => {
    if (!eventContactSelection) {
      return [];
    }

    const contact = contacts.find((item) => item.id === eventContactSelection);
    return contact ? [toContactPickerOption(contact)] : [];
  }, [contacts, eventContactSelection, toContactPickerOption]);

  const availableAdditionalAssetOptions = React.useMemo(() => {
    return filteredAssetOptions;
  }, [filteredAssetOptions]);

  const filteredAssetSelectionOptions = React.useMemo(() => {
    const normalizedSearch = assetSearch.trim().toLowerCase();
    const selectedIds = new Set(eventSelectedAssetIds);

    const base = normalizedSearch
      ? availableAdditionalAssetOptions.filter((asset) => getAssetLabel(asset).toLowerCase().includes(normalizedSearch))
      : availableAdditionalAssetOptions;

    return [...base].sort((left, right) => {
      const leftSelectedWeight = selectedIds.has(left.id) ? 0 : 1;
      const rightSelectedWeight = selectedIds.has(right.id) ? 0 : 1;
      if (leftSelectedWeight !== rightSelectedWeight) {
        return leftSelectedWeight - rightSelectedWeight;
      }

      return getAssetLabel(left).localeCompare(getAssetLabel(right));
    });
  }, [availableAdditionalAssetOptions, assetSearch, eventSelectedAssetIds, getAssetLabel]);

  const visibleAssetOptions = React.useMemo(() => {
    return filteredAssetSelectionOptions.slice(0, assetVisibleCount);
  }, [assetVisibleCount, filteredAssetSelectionOptions]);

  const hasMoreAssets = filteredAssetSelectionOptions.length > assetVisibleCount;

  const toAssetPickerOption = React.useCallback((asset: Asset): CalendarSearchPickerOption => {
    return {
      id: asset.id,
      label: getAssetLabel(asset),
    };
  }, [getAssetLabel]);

  const visibleAssetPickerOptions = React.useMemo(() => {
    return visibleAssetOptions.map(toAssetPickerOption);
  }, [visibleAssetOptions, toAssetPickerOption]);

  const selectedAssetPickerOptions = React.useMemo(() => {
    const assetById = new Map(assets.map((asset) => [asset.id, asset]));
    return eventSelectedAssetIds
      .map((assetId) => assetById.get(assetId))
      .filter((asset): asset is Asset => Boolean(asset))
      .map(toAssetPickerOption);
  }, [assets, eventSelectedAssetIds, toAssetPickerOption]);

  React.useEffect(() => {
    const allowedAssetIds = new Set(filteredAssetOptions.map((asset) => asset.id));
    setEventSelectedAssetIds((previous) => previous.filter((assetId) => allowedAssetIds.has(assetId)));
  }, [filteredAssetOptions]);

  React.useEffect(() => {
    setStaffVisibleCount(ADDITIONAL_STAFF_PAGE_SIZE);
  }, [staffSearch]);

  React.useEffect(() => {
    setContactVisibleCount(ADDITIONAL_CONTACT_PAGE_SIZE);
  }, [contactSearch]);

  React.useEffect(() => {
    if (!isSidePanelOpen) {
      setIsStaffPickerOpen(false);
      setIsContactPickerOpen(false);
      setIsAssetPickerOpen(false);
    }
  }, [isSidePanelOpen]);

  React.useEffect(() => {
    const layout = document.querySelector('.app-layout');
    const main = document.querySelector('.main-content');
    const className = 'calendar-side-panel-open';

    if (isSidePanelOpen) {
      layout?.classList.add(className);
      main?.classList.add(className);
    } else {
      layout?.classList.remove(className);
      main?.classList.remove(className);
    }

    return () => {
      layout?.classList.remove(className);
      main?.classList.remove(className);
    };
  }, [isSidePanelOpen]);

  const toggleStaffSelection = React.useCallback((employeeId: string) => {
    setEventSelectedStaffIds((previous) => {
      if (previous.includes(employeeId)) {
        return previous.filter((staffId) => staffId !== employeeId);
      }

      return Array.from(new Set([...previous, employeeId]));
    });
  }, []);

  const toggleContactSelection = React.useCallback((contactId: string) => {
    setEventContactId((previous) => (previous === contactId ? '' : contactId));
  }, []);

  const toggleAssetSelection = React.useCallback((assetId: string) => {
    setEventSelectedAssetIds((previous) => {
      if (previous.includes(assetId)) {
        return previous.filter((item) => item !== assetId);
      }

      return Array.from(new Set([...previous, assetId]));
    });
  }, []);

  React.useEffect(() => {
    setAssetVisibleCount(ADDITIONAL_ASSET_PAGE_SIZE);
    setAssetSearch('');
  }, [eventContactSelection]);

  React.useEffect(() => {
    setAssetVisibleCount(ADDITIONAL_ASSET_PAGE_SIZE);
  }, [assetSearch]);

  const onSubmitEvent = async () => {
    const startAtUtc = toUtcIso(eventStartLocal);

    if (!startAtUtc) {
      showToast(t('eventRangeInvalid'), 'error');
      return;
    }

    try {
      if (editingEvent) {
        const updated = await updateEvent(editingEvent.id, {
          startAtUtc,
          durationMinutes: eventDurationMinutes,
          allowOverlap: eventAllowOverlap,
          roomId: eventRoomSelection,
          staffId: eventStaffSelection,
          staffIds: eventStaffIdsValue.length > 0 ? eventStaffIdsValue : undefined,
          contactId: eventContactSelection,
          assetId: eventAssetSelection,
          assetIds: eventAssetIdsValue.length > 0 ? eventAssetIdsValue : undefined,
          appointmentTypeId: eventAppointmentTypeSelection,
          status: eventStatus,
          title: eventTitleValue,
          notes: eventNotesValue,
          // An empty object clears the visit place; undefined would leave it untouched.
          visitPlace: eventVisitPlace ?? emptyVisitPlace,
          recurrence: buildRecurrencePayload(eventRecurrence),
          applyToSeries: editingEvent.recurringSeriesId ? applyToSeries : undefined,
        });

        const updatedStart = updated?.startAtUtc ? new Date(updated.startAtUtc) : new Date(startAtUtc);
        if (!Number.isNaN(updatedStart.getTime())) {
          if (calendarViewMode === 'day') {
            goToDay(updatedStart);
          } else {
            goToWeek(updatedStart);
          }
        }

        setEvents((prev) => {
          const fallbackUpdated: CalendarEvent = {
            ...editingEvent,
            startAtUtc,
            endAtUtc: toUtcIso(eventEndLocal),
            roomId: eventRoomSelection,
            staffId: eventStaffSelection,
            staffIds: eventStaffIdsValue,
            contactId: eventContactSelection,
            assetId: eventAssetSelection,
            assetIds: eventAssetIdsValue,
            appointmentTypeId: eventAppointmentTypeSelection,
            status: eventStatus,
            title: eventTitleValue,
            notes: eventNotesValue,
            allowOverlap: eventAllowOverlap,
            visitPlace: eventVisitPlace,
            recurrence: buildRecurrencePayload(eventRecurrence),
          };
          return sortEventsByStart([...prev.filter((event) => event.id !== editingEvent.id), updated || fallbackUpdated]);
        });

        showToast(t('eventUpdated'), 'success');
      } else {
        const created = await createEvent({
          startAtUtc,
          durationMinutes: eventDurationMinutes,
          allowOverlap: eventAllowOverlap,
          roomId: eventRoomSelection,
          staffId: eventStaffSelection,
          staffIds: eventStaffIdsValue.length > 0 ? eventStaffIdsValue : undefined,
          contactId: eventContactSelection,
          assetId: eventAssetSelection,
          assetIds: eventAssetIdsValue.length > 0 ? eventAssetIdsValue : undefined,
          appointmentTypeId: eventAppointmentTypeSelection,
          status: eventStatus,
          title: eventTitleValue,
          notes: eventNotesValue,
          visitPlace: eventVisitPlace,
          ...(hasActiveRecurrence(eventRecurrence) ? { recurrence: buildRecurrencePayload(eventRecurrence) } : {}),
        });

        const createdStart = created?.startAtUtc ? new Date(created.startAtUtc) : new Date(startAtUtc);
        if (!Number.isNaN(createdStart.getTime())) {
          if (calendarViewMode === 'day') {
            goToDay(createdStart);
          } else {
            goToWeek(createdStart);
          }
        }

        if (created) {
          setEvents((prev) => {
            return sortEventsByStart([...prev.filter((event) => event.id !== created.id), created]);
          });
        }

        showToast(t('eventCreated'), 'success');
      }

      setEditingEvent(null);
      if (isSidePanelOpen && sidePanelMode === 'create') {
        setIsSidePanelOpen(false);
      }
      loadEvents();
      loadAvailability();
    } catch (error) {
      showToast(error instanceof Error ? error.message : (editingEvent ? t('failedToUpdateEvent') : t('failedToCreateEvent')), 'error');
    }
  };

  const onEditEvent = (event: CalendarEvent) => {
    const roomId = (event.roomId || '').trim();
    if (roomId) {
      const mappedRoomGroupId = roomGroupIdByRoomId[roomId];
      if (mappedRoomGroupId) {
        setSelectedRoomGroupId(mappedRoomGroupId);
      }
    }

    setEditingEvent(event);
    setEventTitle(event.title || '');
    setEventNotes(event.notes || '');
    setEventVisitPlace(event.visitPlace);
    setEventStatus(event.status);
    setEventRecurrence(recurrenceFromEvent(event));
    setApplyToSeries(false);
    setEventAllowOverlap(Boolean(event.allowOverlap));
    setEventStartLocal(toLocalInputValue(new Date(event.startAtUtc)));
    const eventDuration = getDurationMinutesBetweenDates(new Date(event.startAtUtc), new Date(event.endAtUtc));
    setEventDurationMinutes(eventDuration ? clampEventDurationMinutes(eventDuration) : DEFAULT_EVENT_DURATION_MINUTES);
    setEventRoomId(event.roomId || '');
    const resolvedStaffIds = resolveEventStaffIds(event);
    setEventSelectedStaffIds(resolvedStaffIds);
    setStaffSearch('');
    setStaffVisibleCount(ADDITIONAL_STAFF_PAGE_SIZE);
    setIsStaffPickerOpen(false);
    setEventContactId(event.contactId || '');
    setContactSearch('');
    setContactVisibleCount(ADDITIONAL_CONTACT_PAGE_SIZE);
    setIsContactPickerOpen(false);
    const resolvedAssetIds = resolveEventAssetIds(event);
    setEventSelectedAssetIds(resolvedAssetIds);
    setAssetSearch('');
    setAssetVisibleCount(ADDITIONAL_ASSET_PAGE_SIZE);
    setIsAssetPickerOpen(false);
    setEventAppointmentTypeId(event.appointmentTypeId || '');
    setSidePanelMode('create');
    setIsSidePanelOpen(true);
    setActiveEventMenuKey(null);
  };

  const onDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent(eventId);
      showToast(t('eventDeleted'), 'success');
      loadEvents();
      setActiveEventMenuKey(null);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDeleteEvent'), 'error');
    }
  };

  const onViewEvent = (event: CalendarEvent) => {
    setViewEvent(event);
    setActiveEventMenuKey(null);
  };

  const openEventConsultation = React.useCallback(async (event: CalendarEvent) => {
    try {
      const target = await resolveEventConsultationNavigation(
        event,
        `${location.pathname}${location.search}`,
      );
      navigate(`${target.pathname}${target.search || ''}`, { state: target.state });
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToOpenConsultation', { defaultValue: 'Failed to open consultation.' }), 'error');
    } finally {
      setActiveEventMenuKey(null);
    }
  }, [location.pathname, location.search, navigate, showToast, t]);

  const onChangeEventStatus = async (event: CalendarEvent, nextStatus: CalendarEventStatus) => {
    if (event.status === nextStatus) {
      setActiveEventMenuKey(null);
      return;
    }

    try {
      const updated = await updateEvent(event.id, { status: nextStatus });
      setEvents((prev) => prev.map((item) => {
        if (item.id !== event.id) return item;
        if (updated) return updated;
        return { ...item, status: nextStatus };
      }));
      showToast(t('eventStatusUpdated'), 'success');
      loadEvents();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToUpdateEventStatus'), 'error');
    } finally {
      setActiveEventMenuKey(null);
    }
  };

  const renderEventActions = (event: CalendarEvent, menuKey: string) => (
    <div className={styles.eventMenuWrap} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={styles.eventMenuTrigger}
        onClick={(e) => {
          e.stopPropagation();
          setActiveEventMenuKey((prev) => (prev === menuKey ? null : menuKey));
        }}
        aria-label={t('eventActions')}
        aria-expanded={activeEventMenuKey === menuKey}
      >
        ⋮
      </button>
      {activeEventMenuKey === menuKey && (
        <div className={styles.eventMenu}>
          <button type="button" className={styles.eventMenuItem} onClick={() => onViewEvent(event)}>{t('view')}</button>
          {canAccessConsultations ? (
            <button type="button" className={styles.eventMenuItem} onClick={() => openEventConsultation(event)}>
              {t('openConsultation', { defaultValue: 'Open Consultation Form' })}
            </button>
          ) : null}
          <button type="button" className={styles.eventMenuItem} onClick={() => onEditEvent(event)}>{t('edit', { ns: 'common' })}</button>
          {statusOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={styles.eventMenuItem}
              onClick={() => onChangeEventStatus(event, option.value)}
              disabled={normalizeCalendarEventStatusCode(event.status) === option.value}
            >
              {t('setStatusTo', { defaultValue: 'Set {{status}}', status: option.label })}
            </button>
          ))}
          <button type="button" className={`${styles.eventMenuItem} ${styles.eventMenuDanger}`} onClick={() => onDeleteEvent(event.id)}>
            {t('delete', { ns: 'common' })}
          </button>
        </div>
      )}
    </div>
  );

  const filterEventsByRoomGroup = React.useCallback((inputEvents: CalendarEvent[]) => {
    if (!selectedRoomGroupId) return inputEvents;

    const allowedRoomIds = new Set(roomIdsByGroupId[selectedRoomGroupId] || []);
    if (allowedRoomIds.size === 0) return [];

    return inputEvents.filter((event) => {
      const eventRoomId = (event.roomId || '').trim();
      return eventRoomId ? allowedRoomIds.has(eventRoomId) : false;
    });
  }, [roomIdsByGroupId, selectedRoomGroupId]);

  const filteredEvents = React.useMemo(
    () => filterEventsByRoomGroup(events),
    [events, filterEventsByRoomGroup],
  );

  const loadListEventsForRange = React.useCallback(async (rangeFromLocal: string, rangeToLocal: string) => {
    const data = await listEvents({
      fromUtc: toUtcIso(rangeFromLocal),
      toUtc: toUtcIso(rangeToLocal),
    });

    return filterEventsByRoomGroup(data);
  }, [filterEventsByRoomGroup]);

  const selectedRoomGroupLabel = React.useMemo(() => {
    if (!selectedRoomGroupId) {
      return t('allRoomGroups');
    }

    return roomGroupOptions.find((option) => option.id === selectedRoomGroupId)?.label || selectedRoomGroupId;
  }, [roomGroupOptions, selectedRoomGroupId, t]);

  const eventsByDay = React.useMemo(() => {
    const grouped = new Map<string, CalendarEvent[]>();
    filteredEvents.forEach((event) => {
      const startDate = new Date(event.startAtUtc);
      const key = toDateKey(startDate);
      const current = grouped.get(key) || [];
      current.push(event);
      grouped.set(key, current);
    });

    grouped.forEach((dayEvents) => {
      sortEventsByStart(dayEvents);
    });

    return grouped;
  }, [filteredEvents]);

  const goToWeek = (anyDayInWeek: Date) => {
    const weekStart = startOfWeek(anyDayInWeek);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 0, 0);

    setCalendarWeekStart(weekStart);
    setFromLocal(toLocalInputValue(weekStart));
    setToLocal(toLocalInputValue(weekEnd));
  };

  const goToDay = (anyDate: Date) => {
    const dayStart = new Date(anyDate.getFullYear(), anyDate.getMonth(), anyDate.getDate(), 0, 0, 0, 0);
    const dayEnd = new Date(anyDate.getFullYear(), anyDate.getMonth(), anyDate.getDate(), 23, 59, 0, 0);

    setCalendarDay(dayStart);
    setCalendarWeekStart(startOfWeek(dayStart));
    setFromLocal(toLocalInputValue(dayStart));
    setToLocal(toLocalInputValue(dayEnd));
  };

  const onChangeViewMode = (mode: CalendarViewMode) => {
    setCalendarViewMode(mode);
    if (mode === 'day') {
      const selected = parseLocalDateTime(eventStartLocal);
      const base = selected ?? new Date();
      goToDay(base);
      return;
    }

    const selected = parseLocalDateTime(eventStartLocal);
    const base = selected ?? new Date();
    goToWeek(base);
  };

  const onNavigatePeriod = (direction: -1 | 1) => {
    if (calendarViewMode === 'day') {
      const nextDay = new Date(calendarDay);
      nextDay.setDate(nextDay.getDate() + direction);
      goToDay(nextDay);
      return;
    }

    const nextWeek = new Date(calendarWeekStart);
    nextWeek.setDate(nextWeek.getDate() + (direction * 7));
    goToWeek(nextWeek);
  };

  const selectedTimelineDayKey = React.useMemo(() => toDateKey(calendarDay), [calendarDay]);

  // Drives the "now" line and the today highlight; ticking each minute keeps both correct past midnight.
  const [now, setNow] = React.useState(() => new Date());

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const todayKey = React.useMemo(() => toDateKey(now), [now]);

  const dayTimelineEvents = React.useMemo(() => {
    const dayEvents = eventsByDay.get(selectedTimelineDayKey) || [];
    return sortEventsByStart([...dayEvents]);
  }, [eventsByDay, selectedTimelineDayKey]);

  const filteredAvailabilitySlots = React.useMemo(() => {
    if (!businessHoursOnly) {
      return availabilitySlots;
    }

    return availabilitySlots.filter((slot) => {
      const startAt = new Date(slot.startAtUtc);
      const endAt = new Date(slot.endAtUtc);
      if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime()) || startAt >= endAt) {
        return false;
      }

      const openingStart = getOpeningStartMinute(startAt);
      const openingEnd = getOpeningEndMinute(startAt);
      if (openingStart === null || openingEnd === null) {
        return false;
      }

      const slotStartMinute = startAt.getHours() * 60 + startAt.getMinutes();
      const slotEndMinute = endAt.getHours() * 60 + endAt.getMinutes();
      return slotStartMinute >= openingStart && slotEndMinute <= openingEnd;
    });
  }, [availabilitySlots, businessHoursOnly, getOpeningEndMinute, getOpeningStartMinute]);

  const availabilityDayGroups = React.useMemo(
    () => groupAvailabilitySlotsByDay(
      filteredAvailabilitySlots,
      (startAtUtc) => formatAvailabilityDayHeadingLabel(startAtUtc, translateCalendarDateLabel),
    ),
    [filteredAvailabilitySlots, translateCalendarDateLabel],
  );

  const onSelectAvailabilitySlot = React.useCallback((slot: AvailabilitySlot) => {
    const startAt = new Date(slot.startAtUtc);
    const endAt = new Date(slot.endAtUtc);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime()) || startAt >= endAt) {
      return;
    }

    setEditingEvent(null);
    setEventTitle('');
    setEventNotes('');
    setEventVisitPlace(undefined);
    setEventStatus(defaultEventStatus);
    setEventRecurrence(DEFAULT_CALENDAR_RECURRENCE());
    setApplyToSeries(false);
    setEventAllowOverlap(false);
    setEventStartLocal(toLocalInputValue(startAt));
    const slotDuration = getDurationMinutesBetweenDates(startAt, endAt);
    if (slotDuration) {
      setEventDurationMinutes(clampEventDurationMinutes(slotDuration));
    }
    setEventRoomId(slot.roomId || '');
    setEventSelectedStaffIds(
      slot.staffId
        ? [slot.staffId]
        : (eventStaffIdsValue.length > 0
          ? eventStaffIdsValue
          : (myEmployeeId ? [myEmployeeId] : [])),
    );
    setStaffSearch('');
    setStaffVisibleCount(ADDITIONAL_STAFF_PAGE_SIZE);
    setIsStaffPickerOpen(false);
    setEventContactId('');
    setContactSearch('');
    setContactVisibleCount(ADDITIONAL_CONTACT_PAGE_SIZE);
    setIsContactPickerOpen(false);
    setEventSelectedAssetIds([]);
    setAssetSearch('');
    setAssetVisibleCount(ADDITIONAL_ASSET_PAGE_SIZE);
    setIsAssetPickerOpen(false);
    setSidePanelMode('create');
    setIsSidePanelOpen(true);
  }, [defaultEventStatus, eventStaffIdsValue, myEmployeeId]);

  const dayTimeSlots = React.useMemo(() => {
    const safeSlotMinutes = Math.max(5, Math.min(240, Number.isFinite(slotMinutes) ? slotMinutes : 20));
    const startMinute = businessHoursOnly ? (getOpeningStartMinute(calendarDay) ?? 0) : 0;
    const totalSlots = Math.ceil(((24 * 60) - startMinute) / safeSlotMinutes);

    return Array.from({ length: totalSlots }, (_, index) => {
      const slotStart = new Date(calendarDay);
      slotStart.setHours(0, 0, 0, 0);
      slotStart.setMinutes(startMinute + (index * safeSlotMinutes));

      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + safeSlotMinutes);

      const slotEvents = dayTimelineEvents.filter((event) => {
        const eventStart = new Date(event.startAtUtc);
        // Render an event once at its start slot (avoids duplicate cards in later overlapping slots).
        return eventStart >= slotStart && eventStart < slotEnd;
      });

      return {
        key: `${selectedTimelineDayKey}-${index}`,
        label: `${formatTimeLabel(slotStart)} - ${formatTimeLabel(slotEnd)}`,
        events: slotEvents,
        startAt: slotStart,
        endAt: slotEnd,
        isHourBoundary: ((index * safeSlotMinutes) % 60) === 0,
      };
    });
  }, [businessHoursOnly, calendarDay, dayTimelineEvents, getOpeningStartMinute, selectedTimelineDayKey, slotMinutes]);

  const dayRoomColumnKeys = React.useMemo(() => {
    if (eventRoomSelection) {
      return [eventRoomSelection];
    }

    const knownRoomIds = selectedRoomGroupId
      ? (roomIdsByGroupId[selectedRoomGroupId] || [])
      : Object.keys(roomNameById);

    const orderedRoomIds = Array.from(new Set(knownRoomIds))
      .sort((a, b) => (roomNameById[a] || a).localeCompare(roomNameById[b] || b));

    if (orderedRoomIds.length === 0) {
      return [UNASSIGNED_ROOM_KEY];
    }

    // Keep unassigned visible as a separate column for roomless events.
    return [...orderedRoomIds, UNASSIGNED_ROOM_KEY];
  }, [eventRoomSelection, roomIdsByGroupId, roomNameById, selectedRoomGroupId]);

  const weekTimeSlotsByDay = React.useMemo(() => {
    if (calendarViewMode !== 'week') {
      return [] as Array<{
        date: Date;
        dayKey: string;
        dayLabel: string;
        weekdayLabel: string;
        dayNumberLabel: string;
        monthLabel: string;
        isToday: boolean;
        slots: TimeSlot[];
      }>;
    }

    // Keep week timeline rows fixed to one-hour blocks for readability.
    const weekSlotMinutes = 60;
    const weekDates = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(calendarWeekStart);
      date.setDate(calendarWeekStart.getDate() + index);
      date.setHours(0, 0, 0, 0);
      return date;
    });

    const weekStartMinute = businessHoursOnly
      ? (() => {
        const candidates = weekDates
          .map((date) => getOpeningStartMinute(date))
          .filter((value): value is number => value !== null);
        return candidates.length > 0 ? Math.min(...candidates) : 0;
      })()
      : 0;

    const totalSlots = Math.ceil(((24 * 60) - weekStartMinute) / weekSlotMinutes);

    return Array.from({ length: 7 }, (_, dayIndex) => {
      const date = new Date(calendarWeekStart);
      date.setDate(calendarWeekStart.getDate() + dayIndex);
      date.setHours(0, 0, 0, 0);

      const dayKey = toDateKey(date);
      const dayEvents = sortEventsByStart((eventsByDay.get(dayKey) || []).slice());

      const slots: TimeSlot[] = Array.from({ length: totalSlots }, (_, slotIndex) => {
        const slotStart = new Date(date);
        slotStart.setMinutes(weekStartMinute + (slotIndex * weekSlotMinutes));

        const slotEnd = new Date(slotStart);
        slotEnd.setMinutes(slotEnd.getMinutes() + weekSlotMinutes);

        const slotEvents = dayEvents.filter((event) => {
          const eventStart = new Date(event.startAtUtc);
          // Render an event once at its start slot (avoids duplicate cards in later overlapping slots).
          return eventStart >= slotStart && eventStart < slotEnd;
        });

        return {
          key: `${dayKey}-${slotIndex}`,
          label: `${formatTimeLabel(slotStart)} - ${formatTimeLabel(slotEnd)}`,
          events: slotEvents,
          startAt: slotStart,
          endAt: slotEnd,
          isHourBoundary: true,
        };
      });

      return {
        date,
        dayKey,
        dayLabel: formatWeekDayColumnLabel(date, dayIndex, translateCalendarDateLabel),
        weekdayLabel: translateCalendarDateLabel(`weekdays.${MONDAY_FIRST_WEEKDAY_KEYS[dayIndex]}`),
        dayNumberLabel: String(date.getDate()),
        monthLabel: translateCalendarDateLabel(`monthsShort.${MONTH_I18N_KEYS[date.getMonth()]}`),
        isToday: dayKey === todayKey,
        slots,
      };
    });
  }, [businessHoursOnly, calendarViewMode, calendarWeekStart, eventsByDay, getOpeningStartMinute, todayKey, translateCalendarDateLabel]);

  // Fraction through the slot that currently contains "now", or null when now falls outside it.
  const getNowOffsetWithin = React.useCallback((startAt: Date, endAt: Date): number | null => {
    const span = endAt.getTime() - startAt.getTime();
    if (span <= 0) {
      return null;
    }

    const elapsed = now.getTime() - startAt.getTime();
    if (elapsed < 0 || elapsed >= span) {
      return null;
    }

    return elapsed / span;
  }, [now]);

  const weekTimeAxisLabels = React.useMemo(() => {
    if (calendarViewMode !== 'week' || weekTimeSlotsByDay.length === 0) return [] as string[];

    return weekTimeSlotsByDay[0].slots.map((slot) => {
      const [startLabel] = slot.label.split(' - ');
      if (!startLabel) return '';

      const normalized = startLabel.toLowerCase();
      const minuteMatch = normalized.match(/:(\d{2})/);
      if (!minuteMatch) return startLabel;

      const minute = Number(minuteMatch[1]);
      if (Number.isNaN(minute)) return startLabel;

      return minute === 0 ? startLabel : '';
    });
  }, [calendarViewMode, weekTimeSlotsByDay]);

  const onSelectDate = (dateValue: string) => {
    if (!dateValue) return;
    const picked = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(picked.getTime())) return;

    if (calendarViewMode === 'week') {
      goToWeek(picked);
      return;
    }

    goToDay(picked);
  };

  const onClickCalendarSlot = React.useCallback((startAt: Date, endAt: Date, roomKey?: string) => {
    setEditingEvent(null);
    setEventTitle('');
    setEventNotes('');
    setEventVisitPlace(undefined);
    setEventStatus(defaultEventStatus);
    setEventRecurrence(DEFAULT_CALENDAR_RECURRENCE());
    setApplyToSeries(false);
    setEventAllowOverlap(false);
    setEventStartLocal(toLocalInputValue(startAt));
    const slotDuration = getDurationMinutesBetweenDates(startAt, endAt);
    if (slotDuration) {
      setEventDurationMinutes(clampEventDurationMinutes(slotDuration));
    }
    setEventSelectedStaffIds(eventStaffIdsValue.length > 0 ? eventStaffIdsValue : (myEmployeeId ? [myEmployeeId] : []));
    setStaffSearch('');
    setStaffVisibleCount(ADDITIONAL_STAFF_PAGE_SIZE);
    setIsStaffPickerOpen(false);
    setEventContactId('');
    setContactSearch('');
    setContactVisibleCount(ADDITIONAL_CONTACT_PAGE_SIZE);
    setIsContactPickerOpen(false);
    setEventSelectedAssetIds([]);
    setAssetSearch('');
    setAssetVisibleCount(ADDITIONAL_ASSET_PAGE_SIZE);
    setIsAssetPickerOpen(false);
    setEventAppointmentTypeId('');

    if (roomKey !== undefined) {
      setEventRoomId(roomKey === UNASSIGNED_ROOM_KEY ? '' : roomKey);
    }

    setSidePanelMode('create');
    setIsSidePanelOpen(true);
  }, [defaultEventStatus, eventStaffIdsValue, myEmployeeId]);

  const openSidePanel = (mode: SidePanelMode) => {
    if (mode === 'create') {
      setEditingEvent(null);
      setEventTitle('');
      setEventNotes('');
      setEventVisitPlace(undefined);
      setEventStatus(defaultEventStatus);
    setEventRecurrence(DEFAULT_CALENDAR_RECURRENCE());
    setApplyToSeries(false);
      setEventAllowOverlap(false);
      setEventSelectedStaffIds(eventStaffIdsValue.length > 0 ? eventStaffIdsValue : (myEmployeeId ? [myEmployeeId] : []));
      setStaffSearch('');
      setStaffVisibleCount(ADDITIONAL_STAFF_PAGE_SIZE);
      setIsStaffPickerOpen(false);
      setEventContactId('');
      setContactSearch('');
      setContactVisibleCount(ADDITIONAL_CONTACT_PAGE_SIZE);
      setIsContactPickerOpen(false);
      setEventSelectedAssetIds([]);
      setAssetSearch('');
      setAssetVisibleCount(ADDITIONAL_ASSET_PAGE_SIZE);
      setIsAssetPickerOpen(false);
      setEventAppointmentTypeId('');
      setEventDurationMinutes(DEFAULT_EVENT_DURATION_MINUTES);
    }
    setSidePanelMode(mode);
    setIsSidePanelOpen(true);
    if (mode === 'availability') {
      loadAvailability();
    }
  };

  const closeSidePanel = React.useCallback(() => {
    setIsSidePanelOpen(false);
    setEditingEvent(null);
  }, []);

  const pageClassName = [
    styles.page,
    calendarViewMode === 'day' ? styles.pageDay : styles.pageWeek,
    'contact-list',
    isSidePanelOpen ? styles.pagePanelOpen : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={pageClassName}>
      <div className={styles.stickyHeaderStack}>
        <div className={styles.headerRow}>
          <div className={styles.panelHeaderSlot}>
            <div className={styles.panelHeader} aria-hidden={!isSidePanelOpen}>
              <div className={styles.sidePanelTabsBar}>
                <div
                  className={styles.sidePanelTabs}
                  role="tablist"
                  aria-label={t('sidePanelTabs', { defaultValue: 'Event panel tabs' })}
                >
                  <button
                    type="button"
                    role="tab"
                    id="calendar-side-panel-tab-event"
                    aria-selected={sidePanelMode === 'create'}
                    aria-controls="calendar-side-panel-tabpanel"
                    className={sidePanelMode === 'create' ? styles.sidePanelTabActive : styles.sidePanelTab}
                    onClick={() => setSidePanelMode('create')}
                    disabled={!isSidePanelOpen}
                  >
                    {editingEvent ? t('editEvent') : t('createEvent')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="calendar-side-panel-tab-availability"
                    aria-selected={sidePanelMode === 'availability'}
                    aria-controls="calendar-side-panel-tabpanel"
                    className={sidePanelMode === 'availability' ? styles.sidePanelTabActive : styles.sidePanelTab}
                    onClick={() => {
                      setSidePanelMode('availability');
                      loadAvailability();
                    }}
                    disabled={!isSidePanelOpen}
                  >
                    {t('availability')}
                  </button>
                </div>
                <button
                  type="button"
                  className={`btn btn-secondary ${styles.iconButton}`}
                  onClick={() => setIsAvailabilitySettingsOpen(true)}
                  disabled={!isSidePanelOpen}
                  aria-label={t('availabilitySettings.title')}
                  title={t('availabilitySettings.title')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              </div>
              <div className={styles.panelHeaderActions}>
                {isSidePanelOpen && sidePanelMode === 'availability' ? (
                  <button
                    type="button"
                    className={`btn btn-secondary ${styles.iconButton}`}
                    onClick={() => loadAvailability()}
                    disabled={availabilityLoading || availabilityLoadingMore}
                    aria-label={t('refreshAvailability')}
                    title={t('refreshAvailability')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1 4v6h6" />
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`btn btn-secondary ${styles.closeIconButton}`}
                  onClick={closeSidePanel}
                  disabled={!isSidePanelOpen}
                  aria-label={t('close', { ns: 'common' })}
                  title={t('close', { ns: 'common' })}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M18 6L6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className={styles.topbarCenter}>
          <div className={styles.calendarActions}>
            <button className="btn btn-secondary" onClick={() => onNavigatePeriod(-1)} aria-label={t('previousPeriod')} title={t('previousPeriod')}>&#x2039;</button>
            <button className="btn btn-secondary" onClick={() => {
              if (calendarViewMode === 'week') goToWeek(new Date());
              else goToDay(new Date());
            }}>
              {t('today')}
            </button>
            <button className="btn btn-secondary" onClick={() => onNavigatePeriod(1)} aria-label={t('nextPeriod')} title={t('nextPeriod')}>&#x203A;</button>
          </div>
          <div className={styles.viewModeToggle}>
            <button
              className={calendarViewMode === 'week' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => onChangeViewMode('week')}
            >
              {t('week')}
            </button>
            <button
              className={calendarViewMode === 'day' ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => onChangeViewMode('day')}
            >
              {t('day')}
            </button>
          </div>
          <div className={styles.toolbarFieldSlot}>
            <LocalizedDateInput
              id="calendar-date"
              className={styles.inlineToolbarField}
              value={toDateInputValue(activeAnchorDate)}
              onValueChange={onSelectDate}
              aria-label={t('selectDate')}
              fillWidth
            />
          </div>
          <div className={styles.toolbarFieldSlot}>
            <select
              className={styles.inlineToolbarField}
              value={selectedRoomGroupId}
              onChange={(e) => setSelectedRoomGroupId(e.target.value)}
              aria-label={t('filterByRoomGroup')}
              title={t('filterByRoomGroup')}
            >
              <option value="">{t('allRoomGroups')}</option>
              {roomGroupOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </div>
          <label className={styles.businessHoursToggle} title={t('businessHoursOnly')}>
            <input
              type="checkbox"
              checked={businessHoursOnly}
              onChange={(e) => setBusinessHoursOnly(e.target.checked)}
            />
            {t('businessHoursOnly')}
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsEventsListOpen(true)}
          >
            {t('openEventList')}
          </button>
        </div>
        <div className={styles.topActions}>
          {!isSidePanelOpen ? (
            <button
              type="button"
              className={`btn btn-primary ${styles.scheduleButton}`}
              onClick={() => openSidePanel('create')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M8 2v4" />
                <path d="M16 2v4" />
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M3 10h18" />
                <path d="M12 14v4" />
                <path d="M10 16h4" />
              </svg>
              <span>{t('schedule')}</span>
            </button>
          ) : null}
        </div>
        </div>

        {calendarViewMode === 'day' && (
          <div className={styles.intervalQuickActions}>
            {quickIntervals.map((interval) => (
              <button
                key={interval}
                type="button"
                className={`${styles.intervalBtn} ${slotMinutes === interval ? styles.intervalBtnActive : ''}`}
                onClick={() => setSlotMinutes(interval)}
              >
                {interval}m
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.workspace}>
        <aside className={[styles.sidePanel, sidePanelMode === 'create' ? styles.sidePanelEventForm : ''].filter(Boolean).join(' ')} aria-hidden={!isSidePanelOpen}>
          <div className={styles.sidePanelInner}>
            <div
              className={styles.sidePanelBody}
              role="tabpanel"
              id="calendar-side-panel-tabpanel"
              aria-labelledby={sidePanelMode === 'create' ? 'calendar-side-panel-tab-event' : 'calendar-side-panel-tab-availability'}
            >
              {sidePanelMode === 'create' ? (
              <div className={`${styles.panelContent} ${styles.eventFormPanel}`}>
                <div className={styles.eventFormIntro}>
                  <h3 className={styles.eventFormTitle}>{editingEvent ? t('editEvent') : t('createEvent')}</h3>
                  <p className={styles.eventFormSubtitle}>
                    {editingEvent
                      ? t('editEventHint', { defaultValue: 'Update the event details below.' })
                      : t('createEventHint', { defaultValue: 'Fill in the details to schedule a new event.' })}
                  </p>
                </div>
                <div className="form-row">
                  <label>{t('title')}</label>
                  <input className={`text-input ${styles.eventFormControl}`} value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} />
                </div>
                <div className="form-row">
                  <label>{t('appointmentType', { defaultValue: 'Consultation Type' })}</label>
                  <div className={styles.appointmentTypeField}>
                    <select
                      className={`text-input ${styles.eventFormControl}`}
                      value={eventAppointmentTypeId}
                      onChange={(e) => onChangeEventAppointmentType(e.target.value)}
                    >
                      <option value="">{t('none', { ns: 'common', defaultValue: 'None' })}</option>
                      {appointmentTypes.map((appointmentType) => (
                        <option key={appointmentType.id} value={appointmentType.id}>{getAppointmentTypeLabel(appointmentType)}</option>
                      ))}
                    </select>
                    {canManageConsultationTypes ? (
                      <Link
                        to="/consultations/types"
                        className={styles.manageConsultationTypesLink}
                        title={t('manageConsultationTypes', { ns: 'consultations', defaultValue: 'Manage consultation types' })}
                        aria-label={t('manageConsultationTypes', { ns: 'consultations', defaultValue: 'Manage consultation types' })}
                      >
                        <svg className={styles.manageConsultationTypesIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                          <path
                            d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
                            stroke="currentColor"
                            strokeWidth="1.75"
                          />
                          <path
                            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="form-row">
                  <label>{t('status')}</label>
                  <div className={styles.appointmentTypeField}>
                    <select className={`text-input ${styles.eventFormControl}`} value={eventStatus} onChange={(e) => setEventStatus(e.target.value as CalendarEventStatus)}>
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {canManageEventStatuses ? (
                      <Link
                        to="/calendar/event-statuses"
                        className={styles.manageConsultationTypesLink}
                        title={t('manageEventStatuses', { defaultValue: 'Manage event statuses' })}
                        aria-label={t('manageEventStatuses', { defaultValue: 'Manage event statuses' })}
                      >
                        <svg className={styles.manageConsultationTypesIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                          <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" stroke="currentColor" strokeWidth="1.75" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Link>
                    ) : null}
                  </div>
                </div>
                <CalendarEventRecurrenceFields
                  value={eventRecurrence}
                  onChange={setEventRecurrence}
                  eventStartLocal={eventStartLocal}
                  showApplyToSeries={Boolean(editingEvent?.recurringSeriesId)}
                  applyToSeries={applyToSeries}
                  onApplyToSeriesChange={setApplyToSeries}
                />
                <div className={`form-row ${styles.eventFormCheckboxRow}`}>
                  <label className={styles.eventFormCheckboxLabel}>
                    <input type="checkbox" checked={eventAllowOverlap} onChange={(e) => setEventAllowOverlap(e.target.checked)} />
                    {' '}{t('allowOverlap', { defaultValue: 'Allow overlap' })}
                  </label>
                </div>
                <div className="form-row">
                  <label>{t('staff', { defaultValue: 'Staff' })}</label>
                  <CalendarSearchPicker
                    triggerClassName={styles.eventFormControl}
                    placeholder={t('searchStaffPlaceholder', { defaultValue: 'Search staff by name or email' })}
                    searchPlaceholder={t('searchStaffPlaceholder', { defaultValue: 'Search staff by name or email' })}
                    emptyMessage={t('noAdditionalStaffAvailable', { defaultValue: 'No additional staff available.' })}
                    selectedCountLabel={t('selectedCount', { defaultValue: '{{count}} selected', count: selectedStaffPickerOptions.length })}
                    showingCountLabel={t('showingSelectedCount', {
                      defaultValue: 'Showing {{shown}} of {{total}}',
                      shown: visibleStaffPickerOptions.length,
                      total: filteredAdditionalStaffOptions.length,
                    })}
                    loadMoreLabel={t('loadMoreStaff', { defaultValue: 'Load more staff' })}
                    listAriaLabel={t('staff', { defaultValue: 'Staff' })}
                    visibleOptions={visibleStaffPickerOptions}
                    selectedOptions={selectedStaffPickerOptions}
                    selectedIds={eventSelectedStaffIds}
                    search={staffSearch}
                    onSearchChange={setStaffSearch}
                    onToggle={toggleStaffSelection}
                    hasMore={hasMoreAdditionalStaff}
                    onLoadMore={() => setStaffVisibleCount((previous) => previous + ADDITIONAL_STAFF_PAGE_SIZE)}
                    isOpen={isStaffPickerOpen}
                    onOpenChange={(open) => {
                      setIsStaffPickerOpen(open);
                      if (open) {
                        setIsContactPickerOpen(false);
                        setIsAssetPickerOpen(false);
                      }
                    }}
                    toolbar={(
                      <div className={styles.pickerToolbar}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            if (!myEmployeeId) {
                              showToast(t('currentEmployeeUnavailable'), 'info');
                              return;
                            }

                            setEventSelectedStaffIds((previous) => Array.from(new Set([...previous, myEmployeeId])));
                          }}
                          disabled={employeesLoading}
                        >
                          {t('useMyEmployee')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setEventSelectedStaffIds([])}
                          disabled={eventSelectedStaffIds.length === 0}
                        >
                          {t('clearSelection', { defaultValue: 'Clear selection' })}
                        </button>
                      </div>
                    )}
                    selectedSummaryHint={eventSelectedStaffIds.length > 0 ? (
                      <p className={styles.inlineHint}>
                        {t('selectedCount', { defaultValue: '{{count}} selected', count: eventSelectedStaffIds.length })}
                      </p>
                    ) : null}
                  />
                </div>
                <div className="form-row">
                  <label>{t('contact', { defaultValue: 'Contact' })}</label>
                  <CalendarSearchPicker
                    triggerClassName={styles.eventFormControl}
                    placeholder={t('searchContactPlaceholder', { defaultValue: 'Search contact by name or email' })}
                    searchPlaceholder={t('searchContactPlaceholder', { defaultValue: 'Search contact by name or email' })}
                    emptyMessage={t('noContactsAvailable', { defaultValue: 'No contacts available.' })}
                    selectedCountLabel={t('selectedCount', { defaultValue: '{{count}} selected', count: selectedContactPickerOptions.length })}
                    showingCountLabel={t('showingSelectedCount', {
                      defaultValue: 'Showing {{shown}} of {{total}}',
                      shown: visibleContactPickerOptions.length,
                      total: filteredContactOptions.length,
                    })}
                    loadMoreLabel={t('loadMoreContacts', { defaultValue: 'Load more contacts' })}
                    listAriaLabel={t('contact', { defaultValue: 'Contact' })}
                    visibleOptions={visibleContactPickerOptions}
                    selectedOptions={selectedContactPickerOptions}
                    selectedIds={eventContactSelection ? [eventContactSelection] : []}
                    search={contactSearch}
                    onSearchChange={setContactSearch}
                    onToggle={toggleContactSelection}
                    hasMore={hasMoreContacts}
                    onLoadMore={() => setContactVisibleCount((previous) => previous + ADDITIONAL_CONTACT_PAGE_SIZE)}
                    closeOnSelect
                    isOpen={isContactPickerOpen}
                    onOpenChange={(open) => {
                      setIsContactPickerOpen(open);
                      if (open) {
                        setIsStaffPickerOpen(false);
                        setIsAssetPickerOpen(false);
                      }
                    }}
                    toolbar={(
                      <div className={styles.pickerToolbar}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setEventContactId('')}
                          disabled={!eventContactSelection}
                        >
                          {t('clearSelection', { defaultValue: 'Clear selection' })}
                        </button>
                      </div>
                    )}
                  />
                </div>
                <div className="form-row">
                  <label>{t('asset', { defaultValue: 'Asset' })}</label>
                  <CalendarSearchPicker
                    triggerClassName={styles.eventFormControl}
                    placeholder={eventContactSelection
                      ? t('searchAssetPlaceholder', { defaultValue: 'Search assets' })
                      : t('selectContactForAssets', { defaultValue: 'Select a contact first' })}
                    searchPlaceholder={t('searchAssetPlaceholder', { defaultValue: 'Search assets' })}
                    emptyMessage={assetsLoading
                      ? t('loadingAssets', { defaultValue: 'Loading assets…' })
                      : (eventContactSelection
                        ? t('noAdditionalAssetsAvailable', { defaultValue: 'No additional assets available.' })
                        : t('selectContactForAssets', { defaultValue: 'Select a contact first' }))}
                    selectedCountLabel={t('selectedCount', { defaultValue: '{{count}} selected', count: selectedAssetPickerOptions.length })}
                    showingCountLabel={t('showingSelectedCount', {
                      defaultValue: 'Showing {{shown}} of {{total}}',
                      shown: visibleAssetPickerOptions.length,
                      total: filteredAssetSelectionOptions.length,
                    })}
                    loadMoreLabel={t('loadMoreAssets', { defaultValue: 'Load more assets' })}
                    listAriaLabel={t('asset', { defaultValue: 'Asset' })}
                    visibleOptions={visibleAssetPickerOptions}
                    selectedOptions={selectedAssetPickerOptions}
                    selectedIds={eventSelectedAssetIds}
                    search={assetSearch}
                    onSearchChange={setAssetSearch}
                    onToggle={toggleAssetSelection}
                    hasMore={hasMoreAssets}
                    onLoadMore={() => setAssetVisibleCount((previous) => previous + ADDITIONAL_ASSET_PAGE_SIZE)}
                    disabled={!eventContactSelection || assetsLoading}
                    isOpen={isAssetPickerOpen}
                    onOpenChange={(open) => {
                      if (!eventContactSelection || assetsLoading) {
                        setIsAssetPickerOpen(false);
                        return;
                      }

                      setIsAssetPickerOpen(open);
                      if (open) {
                        setIsStaffPickerOpen(false);
                        setIsContactPickerOpen(false);
                      }
                    }}
                    toolbar={(
                      <div className={styles.pickerToolbar}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            const visibleIds = visibleAssetOptions.map((asset) => asset.id);
                            setEventSelectedAssetIds((previous) => Array.from(new Set([...previous, ...visibleIds])));
                          }}
                          disabled={visibleAssetOptions.length === 0}
                        >
                          {t('selectVisibleAssets', { defaultValue: 'Select visible' })}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setEventSelectedAssetIds([])}
                          disabled={eventSelectedAssetIds.length === 0}
                        >
                          {t('clearSelection', { defaultValue: 'Clear selection' })}
                        </button>
                      </div>
                    )}
                    footerHint={eventContactSelection ? (
                      <p className={styles.inlineHint}>{t('assetsFilteredByContact', { defaultValue: 'Showing assets for selected contact.' })}</p>
                    ) : (
                      <p className={styles.inlineHint}>{t('selectContactForAssetsHint', { defaultValue: 'Choose a contact to load linked assets.' })}</p>
                    )}
                    selectedSummaryHint={eventSelectedAssetIds.length > 0 ? (
                      <p className={styles.inlineHint}>
                        {t('selectedCount', { defaultValue: '{{count}} selected', count: eventSelectedAssetIds.length })}
                      </p>
                    ) : null}
                  />
                </div>
                <div className="form-row">
                  <label>{t('start')}</label>
                  <LocalizedDateTimeInput
                    className={styles.eventFormDateTime}
                    dateClassName={styles.eventFormControl}
                    timeClassName={styles.eventFormControl}
                    value={eventStartLocal}
                    onValueChange={onChangeEventStartLocal}
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="event-duration-minutes">{t('durationMinutes')}</label>
                  <input
                    id="event-duration-minutes"
                    className={`text-input ${styles.eventFormControl}`}
                    type="number"
                    min={5}
                    max={1440}
                    step={5}
                    value={eventDurationMinutes}
                    onChange={(e) => onChangeEventDurationMinutes(e.target.value)}
                  />
                </div>
                <div className="form-row">
                  <label>{t('end')}</label>
                  <LocalizedDateTimeInput
                    className={styles.eventFormDateTime}
                    dateClassName={styles.eventFormControl}
                    timeClassName={styles.eventFormControl}
                    value={eventEndLocal}
                    disabled
                    aria-label={t('end')}
                  />
                </div>
                <div className="form-row">
                  <label>{t('room')}</label>
                  <select
                    className={`text-input ${styles.eventFormControl}`}
                    value={eventRoomId}
                    onChange={(e) => setEventRoomId(e.target.value)}
                  >
                    <option value="">{t('unassigned')}</option>
                    {eventRoomOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>{t('notes')}</label>
                  <textarea className={`text-input ${styles.eventFormControl}`} value={eventNotes} onChange={(e) => setEventNotes(e.target.value)} rows={4} />
                </div>
                <div className={`form-row ${styles.eventFormWideRow}`}>
                  <VisitPlaceFields
                    targetKind="calendarEvent"
                    value={eventVisitPlace}
                    onChange={setEventVisitPlace}
                  />
                </div>
                <div className={styles.eventFormFooter}>
                  <button className="btn btn-primary" onClick={onSubmitEvent}>{editingEvent ? t('saveChanges') : t('createEvent')}</button>
                </div>
              </div>
            ) : (
              <div className={styles.panelContent}>
                <div className={styles.availabilityToolbar}>
                  {filteredAvailabilitySlots.length > 0 ? (
                    <span className={styles.availabilitySummary}>
                      {availabilityHasNext
                        ? t('availabilitySlotsShownWithMore', {
                          defaultValue: 'Showing {{count}} slots — more available',
                          count: filteredAvailabilitySlots.length,
                        })
                        : t('availabilitySlotsShown', {
                          defaultValue: 'Showing {{count}} slots',
                          count: filteredAvailabilitySlots.length,
                        })}
                    </span>
                  ) : null}
                </div>
                {availabilityLoading ? (
                  <Spinner text={t('loadingAvailability')} />
                ) : filteredAvailabilitySlots.length === 0 ? (
                  <div className={styles.availabilityEmpty}>{t('noAvailableSlotsInCurrentRange')}</div>
                ) : (
                  <div className={styles.availabilityList}>
                    {availabilityDayGroups.map((group) => (
                      <section key={group.dayKey} className={styles.availabilityDayGroup}>
                        <h4 className={styles.availabilityDayHeading}>{group.dayLabel}</h4>
                        <div className={styles.availabilityDaySlots}>
                          {group.slots.map((slot) => {
                            const durationMinutes = getAvailabilitySlotDurationMinutes(slot);
                            const slotKey = `${slot.startAtUtc}-${slot.endAtUtc}-${slot.roomId || ''}-${slot.staffId || ''}`;

                            return (
                              <button
                                key={slotKey}
                                type="button"
                                className={styles.availabilitySlotCard}
                                onClick={() => onSelectAvailabilitySlot(slot)}
                                title={t('bookAvailabilitySlot', { defaultValue: 'Book this slot' })}
                              >
                                <div className={styles.availabilitySlotMain}>
                                  <span className={styles.availabilitySlotTime}>
                                    {formatTimeLabel(slot.startAtUtc)}
                                    {' – '}
                                    {formatTimeLabel(slot.endAtUtc)}
                                  </span>
                                  {durationMinutes ? (
                                    <span className={styles.availabilitySlotDuration}>
                                      {t('availabilitySlotDuration', {
                                        defaultValue: '{{minutes}} min',
                                        minutes: durationMinutes,
                                      })}
                                    </span>
                                  ) : null}
                                </div>
                                <div className={styles.availabilitySlotMeta}>
                                  <span>{t('room')}: {getRoomDisplayName(slot.roomId)}</span>
                                  <span>{t('staff')}: {slot.staffId ? getStaffNames([slot.staffId]) : '-'}</span>
                                </div>
                                <span className={styles.availabilitySlotAction}>
                                  {t('bookAvailabilitySlot', { defaultValue: 'Book this slot' })}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
                {availabilityHasNext ? (
                  <button
                    type="button"
                    className={`btn btn-secondary ${styles.availabilityLoadMore}`}
                    onClick={() => void loadMoreAvailability()}
                    disabled={availabilityLoading || availabilityLoadingMore}
                  >
                    {availabilityLoadingMore
                      ? t('loadingMoreAvailability', { defaultValue: 'Loading more…' })
                      : t('loadMoreAvailability', { defaultValue: 'Load more slots' })}
                  </button>
                ) : null}
              </div>
            )}
              </div>
            </div>
        </aside>

        <div className={styles.calendarSurface} onClick={() => setActiveEventMenuKey(null)}>
        {calendarViewMode === 'day' && (
          <div className={styles.dayTimeline}>
            <div
              className={styles.dayTimelineGrid}
              style={{
                gridTemplateColumns: `92px repeat(${dayRoomColumnKeys.length}, minmax(0, 1fr))`,
              }}
            >
              <div className={styles.dayTimeHeaderCell}>{t('time')}</div>
              {dayRoomColumnKeys.map((roomKey) => (
                <div key={`day-room-header-${roomKey}`} className={styles.dayRoomHeaderCell}>
                  {roomKey === UNASSIGNED_ROOM_KEY ? t('unassigned') : getRoomDisplayName(roomKey)}
                </div>
              ))}

              {dayTimeSlots.map((slot) => (
                <React.Fragment key={slot.key}>
                  <div
                    className={`${styles.timeSlotLabel} ${slot.isHourBoundary ? styles.timeSlotLabelHour : ''}`}
                    title={slot.label}
                  >
                    {/* Only the start time is shown; the range would wrap and add no information. */}
                    {slot.label.split(' - ')[0]}
                  </div>
                  {dayRoomColumnKeys.map((roomKey) => {
                    const roomEvents = slot.events.filter((event) => toRoomColumnKey(event.roomId) === roomKey);
                    // Colour marks the hours the company is closed; open hours stay plain.
                    const isClosed = isOutsideBusinessHours(slot.startAt, slot.endAt);
                    const nowOffset = selectedTimelineDayKey === todayKey
                      ? getNowOffsetWithin(slot.startAt, slot.endAt)
                      : null;

                    return (
                      <div
                        key={`${slot.key}-${roomKey}`}
                        className={[
                          styles.dayRoomCell,
                          styles.clickableSlot,
                          slot.isHourBoundary ? styles.hourBoundary : '',
                          isClosed ? styles.outsideBusinessHoursSlot : '',
                          nowOffset === null ? '' : styles.nowSlot,
                        ].filter(Boolean).join(' ')}
                        style={nowOffset === null
                          ? undefined
                          : ({ '--calendar-now-offset': `${nowOffset * 100}%` } as React.CSSProperties)}
                        onClick={() => onClickCalendarSlot(slot.startAt, slot.endAt, roomKey)}
                      >
                        <div className={styles.timeSlotEvents}>
                          {roomEvents.length === 0 ? (
                            // Matches the week grid: an empty slot reads as empty, repeating "no events" is noise.
                            <div className={styles.timeSlotEmpty} aria-label={isClosed ? undefined : t('noEvents')} />
                          ) : (
                            roomEvents.map((event) => (
                              <div
                                key={`${slot.key}-${roomKey}-${event.id}`}
                                className={`${styles.timeSlotEventItem} ${canAccessConsultations ? styles.timeSlotEventItemClickable : ''} ${getTimelineStatusClass(event.status, styles)} ${activeEventMenuKey === `day-${slot.key}-${roomKey}-${event.id}` ? styles.timeSlotEventItemMenuOpen : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewEvent(event);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') {
                                    return;
                                  }

                                  e.preventDefault();
                                  e.stopPropagation();
                                  onViewEvent(event);
                                }}
                                role={canAccessConsultations ? 'button' : undefined}
                                tabIndex={canAccessConsultations ? 0 : undefined}
                                title={canAccessConsultations ? t('openConsultation', { defaultValue: 'Open Consultation Form' }) : undefined}
                                style={{
                                  width: '100%',
                                }}
                              >
                                <div className={styles.timeSlotEventHeader}>
                                  <div className={styles.timeSlotEventTitle}>{getEventDisplayTitle(event)} ({getStatusLabel(event.status)})</div>
                                  {renderEventActions(event, `day-${slot.key}-${roomKey}-${event.id}`)}
                                </div>
                                <div className={styles.meta}>
                                  {formatTimeLabel(event.startAtUtc)}
                                  {' - '}
                                  {formatTimeLabel(event.endAtUtc)}
                                  {' • '}{t('room')}: {getRoomDisplayName(event.roomId)}{' • '}{t('staff')}: {getStaffNames(resolveEventStaffIds(event))}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {calendarViewMode === 'week' && (
          <div className={styles.weekTimeline}>
            <div className={styles.weekTimelineGrid}>
              <div className={styles.weekTimeHeaderCell}>{t('time')}</div>
              {weekTimeSlotsByDay.map((day) => (
                <div
                  key={`header-${day.dayKey}`}
                  className={[
                    styles.weekGridHeaderCell,
                    isClosedAllDay(day.date) ? styles.closedDayHeader : '',
                    day.isToday ? styles.todayHeaderCell : '',
                  ].filter(Boolean).join(' ')}
                >
                  <span className={styles.weekDayName}>{day.weekdayLabel}</span>
                  <span className={styles.weekDayDate}>
                    <span className={styles.weekDayNumber}>{day.dayNumberLabel}</span>
                    <span className={styles.weekDayMonth}>{day.monthLabel}</span>
                  </span>
                </div>
              ))}

              {weekTimeAxisLabels.map((timeLabel, slotIndex) => (
                <React.Fragment key={`row-${slotIndex}`}>
                  <div className={styles.weekTimeAxisCell}>
                    {timeLabel || '\u00A0'}
                  </div>
                  {weekTimeSlotsByDay.map((day) => {
                    const slot = day.slots[slotIndex];
                    if (!slot) {
                      return (
                        <div
                          key={`empty-${day.dayKey}-${slotIndex}`}
                          className={[
                            styles.weekSlotCell,
                            day.isToday ? styles.todayColumnCell : '',
                          ].filter(Boolean).join(' ')}
                        />
                      );
                    }

                    // Colour marks the hours the company is closed; open hours stay plain.
                    const isClosed = isOutsideBusinessHours(slot.startAt, slot.endAt);
                    const nowOffset = day.isToday ? getNowOffsetWithin(slot.startAt, slot.endAt) : null;

                    return (
                      <div
                        key={`slot-${day.dayKey}-${slot.key}`}
                        className={[
                          styles.weekSlotCell,
                          styles.clickableSlot,
                          isClosed ? styles.outsideBusinessHoursSlot : '',
                          day.isToday ? styles.todayColumnCell : '',
                          nowOffset === null ? '' : styles.nowSlot,
                        ].filter(Boolean).join(' ')}
                        style={nowOffset === null
                          ? undefined
                          : ({ '--calendar-now-offset': `${nowOffset * 100}%` } as React.CSSProperties)}
                        onClick={() => onClickCalendarSlot(slot.startAt, slot.endAt)}
                      >
                        <div className={styles.timeSlotEvents}>
                          {slot.events.length === 0 ? (
                            <div className={styles.weekSlotEmpty} />
                          ) : (
                            slot.events.map((event) => (
                              <div
                                key={`${slot.key}-${event.id}`}
                                className={`${styles.timeSlotEventItem} ${canAccessConsultations ? styles.timeSlotEventItemClickable : ''} ${getTimelineStatusClass(event.status, styles)} ${activeEventMenuKey === `week-${slot.key}-${event.id}` ? styles.timeSlotEventItemMenuOpen : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewEvent(event);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') {
                                    return;
                                  }

                                  e.preventDefault();
                                  e.stopPropagation();
                                  onViewEvent(event);
                                }}
                                role={canAccessConsultations ? 'button' : undefined}
                                tabIndex={canAccessConsultations ? 0 : undefined}
                                title={canAccessConsultations ? t('openConsultation', { defaultValue: 'Open Consultation Form' }) : undefined}
                                style={{
                                  width: '100%',
                                }}
                              >
                                <div className={styles.timeSlotEventHeader}>
                                  <div className={styles.timeSlotEventTitle}>{getEventDisplayTitle(event)} ({getStatusLabel(event.status)})</div>
                                  {renderEventActions(event, `week-${slot.key}-${event.id}`)}
                                </div>
                                <div className={styles.meta}>
                                  {formatTimeLabel(event.startAtUtc)}
                                  {' - '}
                                  {formatTimeLabel(event.endAtUtc)}
                                  {' • '}{t('room')}: {getRoomDisplayName(event.roomId)}{' • '}{t('staff')}: {getStaffNames(resolveEventStaffIds(event))}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className={`${styles.meta} ${styles.loadingStatus} ${eventsLoading ? '' : styles.loadingStatusHidden}`}>
          {t('refreshingEventData')}
        </div>
        </div>
      </div>

      {viewEvent && createPortal(
        <div
          className={styles.topModalOverlay}
          onClick={() => setViewEvent(null)}
          role="presentation"
        >
          <div
            className={styles.topModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="calendar-event-details-title"
            onClick={event => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 id="calendar-event-details-title" className={styles.queryTitle}>{t('eventDetails')}</h3>
              <button className="btn btn-secondary" onClick={() => setViewEvent(null)}>{t('close', { ns: 'common' })}</button>
            </div>
            <div className={styles.list}>
              <div className={styles.row}>
                <div><strong>{t('title')}:</strong> {getEventDisplayTitle(viewEvent)}</div>
                <div><strong>{t('status')}:</strong> {getStatusLabel(viewEvent.status)}</div>
                <div><strong>{t('start')}:</strong> {formatLocalizedDateTimeLabel(viewEvent.startAtUtc)}</div>
                <div><strong>{t('end')}:</strong> {formatLocalizedDateTimeLabel(viewEvent.endAtUtc)}</div>
                {viewEvent.recurrence && viewEvent.recurrence.frequency !== 'none' ? (
                  <div><strong>{t('recurrence')}:</strong> {formatRecurrenceSummary(viewEvent.recurrence, t)}</div>
                ) : null}
                <div><strong>{t('room')}:</strong> {getRoomDisplayName(viewEvent.roomId)}</div>
                  <div><strong>{t('staff')}:</strong> {getStaffNames(resolveEventStaffIds(viewEvent))}</div>
                  <div><strong>{t('contact', { defaultValue: 'Contact' })}:</strong> {contacts.find((contact) => contact.id === viewEvent.contactId)?.name || viewEvent.contactId || '-'}</div>
                  <div>
                    <strong>{t('asset', { defaultValue: 'Asset' })}:</strong> {
                      resolveEventAssetIds(viewEvent).length === 0
                        ? '-'
                        : resolveEventAssetIds(viewEvent)
                          .map((assetId) => viewEventAssets.find((asset) => asset.id === assetId)?.name || assetId)
                          .join(', ')
                    }
                  </div>
                  <div><strong>{t('appointmentType', { defaultValue: 'Consultation Type' })}:</strong> {appointmentTypes.find((appointmentType) => appointmentType.id === viewEvent.appointmentTypeId)?.name || viewEvent.appointmentTypeId || '-'}</div>
                  <div><strong>{t('notes')}:</strong> {viewEvent.notes || '-'}</div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  onEditEvent(viewEvent);
                  setViewEvent(null);
                }}
              >
                {t('editEvent')}
              </button>
              {canAccessConsultations ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    openEventConsultation(viewEvent);
                    setViewEvent(null);
                  }}
                >
                  {viewEventHasConsultation
                    ? t('viewConsultation', { defaultValue: 'Open Consultation Form' })
                    : t('createConsultationAction', { defaultValue: 'Create Consultation Form' })}
                </button>
              ) : null}
              <button className="btn btn-secondary" onClick={() => setViewEvent(null)}>{t('close', { ns: 'common' })}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <CalendarEventsListModal
        isOpen={isEventsListOpen}
        onClose={() => setIsEventsListOpen(false)}
        initialFromLocal={fromLocal}
        initialToLocal={toLocal}
        roomGroupLabel={selectedRoomGroupLabel}
        loadEventsForRange={loadListEventsForRange}
        employees={employees}
        contacts={contacts}
        getRoomDisplayName={getRoomDisplayName}
        getStaffNames={getStaffNames}
        getEventDisplayTitle={getEventDisplayTitle}
        onViewEvent={onViewEvent}
      />

      <CalendarAvailabilitySettingsModal
        isOpen={isAvailabilitySettingsOpen}
        canManage={canManageCalendar}
        onClose={() => setIsAvailabilitySettingsOpen(false)}
        onSaved={() => {
          if (isSidePanelOpen && sidePanelMode === 'availability') {
            void loadAvailability({ silent: true });
          }
        }}
      />

    </div>
  );
};

export default CalendarPage;
